import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Zap, Globe, Lock, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import { useI18n } from "@/lib/i18n";

export default function AuthShell({ title, subtitle, children }) {
  const { t, lang } = useI18n();
  const de = lang === "de";

  const headline = de
    ? ["Websites.", "Mobile Apps.", "Software."]
    : ["Websites.", "Mobile Apps.", "Software."];
  const intro = de
    ? "Ihr Partner für professionelle digitale Lösungen — melden Sie sich an und verwalten Sie Ihre Projekte, Angebote und Rechnungen an einem Ort."
    : "Your partner for professional digital solutions — sign in to manage your projects, quotes and invoices in one place.";

  const perks = [
    { icon: ShieldCheck, label: de ? "DSGVO-konform & sicher" : "GDPR-compliant & secure" },
    { icon: Lock, label: de ? "SSL-verschlüsselter Zugang" : "SSL-encrypted access" },
    { icon: Zap, label: de ? "Blitzschnelles Kundenportal" : "Lightning-fast client portal" },
    { icon: Globe, label: de ? "Deutsch & Englisch" : "German & English" },
  ];

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 grain overflow-x-hidden">
      {/* LEFT — brand / trust panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-card border-r border-border p-12 xl:p-16 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/25 blur-[130px]" />
        <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-[130px]" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />

        <div className="relative">
          <Logo />
        </div>

        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
            <Sparkles size={14} /> {de ? "Premium Software-Agentur" : "Premium Software Agency"}
          </span>
          <h2 className="mt-6 font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[1.02]">
            {headline.map((line, i) => (
              <span key={i} className={`block ${i === headline.length - 1 ? "text-primary" : ""}`}>{line}</span>
            ))}
          </h2>
          <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">{intro}</p>

          <ul className="mt-9 space-y-3.5">
            {perks.map(({ icon: I, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm font-medium">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <I size={17} />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center justify-between text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} JonuzovicDesign</span>
          <span className="inline-flex items-center gap-1.5"><Lock size={13} /> {de ? "Sichere Verbindung" : "Secure connection"}</span>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex flex-col p-6 sm:p-12 min-w-0 overflow-hidden">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium link-muted hover:text-foreground transition-colors whitespace-nowrap truncate min-w-0" data-testid="auth-back-home">
            <ArrowLeft size={16} className="shrink-0" /> <span className="truncate">{t("auth.backHome")}</span>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageToggle /><ThemeToggle />
          </div>
        </div>
        <div className="lg:hidden mt-8"><Logo /></div>
        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-sm min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground mb-5 lg:hidden">
              <Sparkles size={12} className="text-primary" /> {de ? "Kundenportal" : "Client Portal"}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight break-words">{title}</h1>
            {subtitle && <p className="text-muted-foreground mt-2.5 mb-8">{subtitle}</p>}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
